import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import { PilotTrack, TrackPoint, TrackRenderOptions, HistoricalTrackRequest } from '../types/tracks';
import { PilotData } from '../types';
import { getWsUrl } from '../config';

export class TrackManager {
  private map: MapLibreMap | null = null;
  private tracks: Map<string, PilotTrack> = new Map();
  private pilots: Map<string, PilotData> = new Map();
  private renderOptions: TrackRenderOptions = {
    showLive: true,
    showHistorical: false,
    liveOpacity: 0.85,
    historicalOpacity: 0.5,
    liveWidth: 4,
    historicalWidth: 2
  };
  private maxPointsPerTrack = 1000;
  private historicalDataCache: Map<string, TrackPoint[]> = new Map();
  private styleChangeTimeout: NodeJS.Timeout | null = null;

  constructor() {
    this.tracks = new Map();
  }

  setMap(map: MapLibreMap | null) {
    // Only set up if we're getting a new map instance
    if (map === this.map) {
      // Same map instance, skipping setup
      return;
    }

    // Clean up old listeners if switching maps
    if (this.map) {
      this.map.off('style.load', this.handleStyleLoad);
      this.map.off('styledata', this.handleStyleData);
    }

    this.map = map;
    if (map) {
      // Setting up new map instance
      // Set up persistent event listeners for style changes
      this.setupStyleChangeListeners();
      // Initial layer setup
      this.initializeLayers();
    }
  }

  private setupStyleChangeListeners() {
    if (!this.map) return;

    // Setting up style change listeners

    // Remove any existing listeners first (defensive)
    this.map.off('style.load', this.handleStyleLoad);
    this.map.off('styledata', this.handleStyleData);

    // Add new listeners
    this.map.on('style.load', this.handleStyleLoad);
    this.map.on('styledata', this.handleStyleData);
  }

  private handleStyleLoad = () => {
    // Style load event detected
    this.onStyleChange();
  }

  private handleStyleData = () => {
    if (this.map && this.map.isStyleLoaded()) {
      // Style data event with loaded style
      // Only process if we haven't already processed a recent style change
      if (!this.styleChangeTimeout) {
        this.onStyleChange();
      }
    }
  }

  private initializeLayers() {
    if (!this.map) {
      // No map available
      return;
    }

    // Wait for map to be loaded and style to be ready
    if (!this.map.loaded || !this.map.isStyleLoaded || !this.map.loaded() || !this.map.isStyleLoaded()) {
      // Map or style not ready, waiting
      setTimeout(() => this.initializeLayers(), 100);
      return;
    }

    // Check if layers already exist and are working
    if (this.map.getSource && this.map.getLayer &&
        this.map.getSource('pilot-tracks-live') && this.map.getLayer('pilot-tracks-live-lines')) {
      // Track layers already exist
      // Just update the rendering with existing data
      this.updateTrackRendering();
      return;
    }

    try {
      // Initializing track layers

      // Remove existing layers first (if they exist)
      const layersToRemove = ['pilot-tracks-live-lines', 'pilot-tracks-historical-lines'];
      layersToRemove.forEach(layerId => {
        if (this.map?.getLayer(layerId)) {
          this.map.removeLayer(layerId);
          // Removed layer
        }
      });

      // Remove existing sources
      const sourcesToRemove = ['pilot-tracks-live', 'pilot-tracks-historical'];
      sourcesToRemove.forEach(sourceId => {
        if (this.map?.getSource(sourceId)) {
          this.map.removeSource(sourceId);
          // Removed source
        }
      });

      // Re-create sources
      // Creating new track sources
      this.map.addSource('pilot-tracks-live', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      this.map.addSource('pilot-tracks-historical', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      // Add historical tracks layer (rendered first, below live tracks)
      this.addTrackLayer('pilot-tracks-historical-lines', 'pilot-tracks-historical', {
        opacity: this.renderOptions.historicalOpacity || 0.5,
        width: this.renderOptions.historicalWidth || 2,
        dashArray: [2, 2]  // Dashed line for historical
      });

      // Add live tracks layer
      // Creating live tracks layer
      this.addTrackLayer('pilot-tracks-live-lines', 'pilot-tracks-live', {
        opacity: this.renderOptions.liveOpacity || 0.85,
        width: this.renderOptions.liveWidth || 4,
        dashArray: null  // Solid line for live
      });

      // Track layers initialized

      // CRITICAL: Force immediate update of track rendering with preserved data
      // Use a small timeout to ensure the layers are fully ready
      setTimeout(() => {
        // Restoring track data
        this.updateTrackRendering();
      }, 50);
    } catch (error) {
      console.error('❌ Error initializing track layers:', error);
      // Retry after a delay
      setTimeout(() => this.initializeLayers(), 500);
    }
  }

  private addTrackLayer(layerId: string, sourceId: string, options: any) {
    if (!this.map) return;

    const layers = this.map.getStyle().layers;
    let beforeId: string | undefined;

    for (const layer of layers || []) {
      if (layer.id.includes('label') ||
          layer.id.includes('symbol') ||
          layer.id.includes('text')) {
        beforeId = layer.id;
        break;
      }
    }

    const layerConfig: any = {
      id: layerId,
      type: 'line',
      source: sourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        'visibility': 'visible'
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8, options.width * 0.7,
          12, options.width,
          16, options.width * 1.5
        ],
        'line-opacity': options.opacity,
        'line-blur': 0.5
      }
    };

    if (options.dashArray) {
      layerConfig.paint!['line-dasharray'] = options.dashArray;
    }

    this.map.addLayer(layerConfig, beforeId);
  }

  addOrUpdatePilot(pilot: PilotData) {
    // Store pilot data
    this.pilots.set(pilot.id, pilot);

    let track = this.tracks.get(pilot.id);

    if (!track) {
      // New pilot - create track with first point
      track = {
        pilotId: pilot.id,
        pilotName: pilot.name,
        color: pilot.color,
        livePoints: [],
        historicalPoints: undefined,
        lastUpdate: 0,
        isHistoryLoaded: false,
        isHistoryLoading: false
      };
      this.tracks.set(pilot.id, track);
    }

    // Update metadata
    track.color = pilot.color;
    track.pilotName = pilot.name;

    // Check if pilot has track history with multiple points
    if (pilot.trackHistory && Array.isArray(pilot.trackHistory) && pilot.trackHistory.length > 0) {
      // Process all track points from history
      const newPoints: TrackPoint[] = [];
      const existingTimestamps = new Set(track.livePoints.map(p => p.timestamp));

      pilot.trackHistory.forEach((histPoint: any) => {
        // Only add points that aren't already in the track (avoid duplicates)
        if (!existingTimestamps.has(histPoint.timestamp)) {
          newPoints.push({
            lng: histPoint.lng,
            lat: histPoint.lat,
            altitude: histPoint.altitude || 0,
            timestamp: histPoint.timestamp,
            speed: histPoint.speed || 0,
            vario: histPoint.vario || 0,
            heading: histPoint.heading || 0
          });
        }
      });

      if (newPoints.length > 0) {
        // Merge new points with existing, sort by timestamp
        track.livePoints = [...track.livePoints, ...newPoints]
          .sort((a, b) => a.timestamp - b.timestamp);

        // Keep only last N points to avoid memory issues
        if (track.livePoints.length > this.maxPointsPerTrack) {
          track.livePoints = track.livePoints.slice(-this.maxPointsPerTrack);
        }

        track.lastUpdate = pilot.timestamp;
        // Track updated
      }
    } else {
      // Fallback to single point update (legacy behavior)
      const lastPoint = track.livePoints[track.livePoints.length - 1];
      const isNewPosition = !lastPoint ||
          Math.abs(lastPoint.lng - pilot.lng) > 0.000001 ||
          Math.abs(lastPoint.lat - pilot.lat) > 0.000001;

      if (isNewPosition) {
        // Add this position to the track
        track.livePoints.push({
          lng: pilot.lng,
          lat: pilot.lat,
          altitude: pilot.altitude,
          timestamp: pilot.timestamp,
          speed: pilot.speed,
          vario: pilot.vario,
          heading: pilot.heading
        });

        // Keep only last N points to avoid memory issues
        if (track.livePoints.length > this.maxPointsPerTrack) {
          track.livePoints = track.livePoints.slice(-this.maxPointsPerTrack);
        }

        track.lastUpdate = pilot.timestamp;
      }
    }

    // Only update rendering if map and sources exist (they might not during style transitions)
    if (this.map && this.map.getSource && this.map.getSource('pilot-tracks-live')) {
      this.updateTrackRendering();
    } else if (this.map && this.map.isStyleLoaded && this.map.isStyleLoaded()) {
      // Track sources not ready
      // Sources don't exist - initialize them first
      // Reinitializing missing sources
      this.initializeLayers();
    }
  }

  toggleHistoricalTrack(pilotId: string, show: boolean): void {
    if (!this.map) return;

    const track = this.tracks.get(pilotId);
    if (!track || !track.mvtSourceId) return;

    const visibility = show ? 'visible' : 'none';

    // Toggle visibility of the MVT layers
    if (this.map.getLayer && this.map.getLayer(`${track.mvtSourceId}-lines`)) {
      this.map.setLayoutProperty(`${track.mvtSourceId}-lines`, 'visibility', visibility);
    }
    if (this.map.getLayer && this.map.getLayer(`${track.mvtSourceId}-points`)) {
      this.map.setLayoutProperty(`${track.mvtSourceId}-points`, 'visibility', visibility);
    }

    track.showingHistory = show;
    // Toggle historical track visibility
  }

  async loadHistoricalTrack(request: HistoricalTrackRequest): Promise<void> {
    // Loading historical track
    if (!this.map) {
      console.warn('Map not initialized yet');
      return;
    }

    const track = this.tracks.get(request.pilotId);
    if (!track) {
      console.warn(`No track found for pilot ${request.pilotId}`);
      return;
    }

    // Check cache first
    const cacheKey = `${request.pilotId}-${request.raceId}`;
    if (this.historicalDataCache.has(cacheKey)) {
      track.historicalPoints = this.historicalDataCache.get(cacheKey);
      track.isHistoryLoaded = true;
      this.updateTrackRendering();
      return;
    }

    if (track.isHistoryLoading) {
      return; // Already loading
    }

    track.isHistoryLoading = true;
    // Loading history
    this.updateTrackRendering(); // Force update to show spinner

    try {
      // Fetching from MVT endpoint
      // Fetch historical data from MVT endpoint
      // For now, we'll add MVT layers dynamically for this specific pilot
      await this.loadMVTHistoricalData(request.raceId, request.pilotId);

      track.isHistoryLoaded = true;
      track.isHistoryLoading = false;
      this.updateTrackRendering(); // Force update to hide spinner

      // Store in cache
      if (track.historicalPoints) {
        this.historicalDataCache.set(cacheKey, track.historicalPoints);
      }

      this.updateTrackRendering();
    } catch (error) {
      console.error('Error loading historical track:', error);
      track.isHistoryLoading = false;
    }
  }

  private async loadMVTHistoricalData(raceId: string, pilotId: string): Promise<void> {
    if (!this.map) return;

    const baseUrl = getWsUrl('').replace('ws://', 'http://').replace('wss://', 'https://').replace(/\/$/, '');
    // MVT endpoint parameters

    // Test fetch a specific tile manually to see what we get
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    // Log the endpoint we're using
    // Using MVT endpoint with live source

    // Get the track to access pilot data
    const track = this.tracks.get(pilotId);
    if (!track) {
      console.error('Track not found');
      return;
    }

    // Build the MVT URL template for MapLibre
    const mvtUrlTemplate = `${baseUrl}/tracking/public-mvt/${raceId}/{z}/{x}/{y}?source=live&date=${today}&pilot_id=${pilotId}`;
    // MVT URL configured

    // Get pilot's current position
    const pilot = this.pilots.get(pilotId);
    if (!pilot) {
      console.error('Pilot not found');
      track.isHistoryLoading = false;
      return;
    }

    // Store current view state
    const currentCenter = this.map.getCenter();
    const currentZoom = this.map.getZoom();

    // Move map to pilot's location to trigger tile loading
    // Moving to pilot location
    this.map.jumpTo({
      center: [pilot.lng, pilot.lat],
      zoom: 15
    });

    // Add temporary MVT source for this pilot's historical data
    const sourceId = `historical-mvt-${pilotId}`;

    if (this.map && !this.map.getSource(sourceId)) {
      // Adding MVT source
      this.map.addSource(sourceId, {
        type: 'vector',
        tiles: [mvtUrlTemplate],
        minzoom: 4,
        maxzoom: 22
      });

      // Add layers to render the MVT data
      // Add track lines layer (initially hidden)
      this.map.addLayer({
        id: `${sourceId}-lines`,
        type: 'line',
        source: sourceId,
        'source-layer': 'track_lines',
        layout: {
          'visibility': 'none'  // Hidden by default
        },
        paint: {
          'line-color': track.color || '#FF0000',
          'line-width': 2,
          'line-opacity': 0.6
        }
      });

      // Add track points layer (initially hidden)
      this.map.addLayer({
        id: `${sourceId}-points`,
        type: 'circle',
        source: sourceId,
        'source-layer': 'track_points',
        layout: {
          'visibility': 'none'  // Hidden by default
        },
        paint: {
          'circle-radius': 3,
          'circle-color': track.color || '#FF0000',
          'circle-opacity': 0.4
        }
      });

      // Wait for tiles to load and render
      // Waiting for MVT tiles
      await new Promise(resolve => {
        let checkCount = 0;
        const checkInterval = setInterval(() => {
          checkCount++;

          // Query both layers (check map exists first)
          if (!this.map) {
            clearInterval(checkInterval);
            resolve(false);
            return;
          }

          const lineFeatures = this.map.querySourceFeatures(sourceId, {
            sourceLayer: 'track_lines'
          });
          const pointFeatures = this.map.querySourceFeatures(sourceId, {
            sourceLayer: 'track_points'
          });

          // Checking for features

          // If we found features or tried 10 times, stop waiting
          if (lineFeatures.length > 0 || pointFeatures.length > 0 || checkCount >= 10) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 500);
      });

      // Query the features from both layers
      const lineFeatures = this.map.querySourceFeatures(sourceId, {
        sourceLayer: 'track_lines'
      });
      const pointFeatures = this.map.querySourceFeatures(sourceId, {
        sourceLayer: 'track_points'
      });

      // Features found

      // Extract points for this pilot (track was already retrieved above)
      if (track) {
        const historicalPoints: TrackPoint[] = [];

        // Process track points (these have timestamps and elevation)
        if (pointFeatures.length > 0) {
          // Processing point features

          // Sort points by point_num if available
          const sortedPoints = pointFeatures.sort((a: any, b: any) => {
            const numA = a.properties?.point_num || 0;
            const numB = b.properties?.point_num || 0;
            return numA - numB;
          });

          sortedPoints.forEach((feature: any) => {
            if (feature.geometry?.type === 'Point') {
              const [lng, lat] = feature.geometry.coordinates;
              const elevation = feature.properties?.elevation || 0;
              const datetime = feature.properties?.datetime;

              historicalPoints.push({
                lng,
                lat,
                altitude: elevation,
                timestamp: datetime ? new Date(datetime).getTime() : Date.now()
              });
            }
          });
        }

        // If no points, try to extract from line features
        if (historicalPoints.length === 0 && lineFeatures.length > 0) {
          // Processing line features

          lineFeatures.forEach((feature: any) => {
            if (feature.geometry?.type === 'LineString') {
              feature.geometry.coordinates.forEach((coord: number[]) => {
                historicalPoints.push({
                  lng: coord[0],
                  lat: coord[1],
                  altitude: coord[2] || 0,
                  timestamp: Date.now()
                });
              });
            }
          });
        }

        if (historicalPoints.length > 0) {
          // Historical points loaded
          track.historicalPoints = historicalPoints;
          track.isHistoryLoaded = true;
        } else {
          // No historical points found
        }

        track.isHistoryLoading = false;
      }

      // Keep the layers if we found historical data, otherwise clean up
      if (!track || !track.historicalPoints || track.historicalPoints.length === 0) {
        // Clean up temporary layers and source
        this.map.removeLayer(`${sourceId}-lines`);
        this.map.removeLayer(`${sourceId}-points`);
        this.map.removeSource(sourceId);
      } else {
        // Store the source ID for later toggling
        track.mvtSourceId = sourceId;
        // Historical data loaded
      }

      // Restore the original view
      // Restoring view
      this.map.jumpTo({
        center: currentCenter,
        zoom: currentZoom
      });
    }
  }

  private updateTrackRendering() {
    if (!this.map) {
      // No map available
      return;
    }

    // Check if sources exist, if not try to initialize first
    if (!this.map.getSource || !this.map.getSource('pilot-tracks-live')) {
      // Initializing layers
      this.initializeLayers();
      return;
    }

    const liveFeatures: any[] = [];
    const historicalFeatures: any[] = [];

    this.tracks.forEach((track) => {
      // Live track features
      if (this.renderOptions.showLive && track.livePoints.length >= 2) {
        const coordinates = track.livePoints.map(p => [p.lng, p.lat, p.altitude || 0]);

        // Rendering track

        liveFeatures.push({
          type: 'Feature',
          properties: {
            pilotId: track.pilotId,
            pilotName: track.pilotName,
            color: track.color,
            trackType: 'live'
          },
          geometry: {
            type: 'LineString',
            coordinates
          }
        });
      }

      // Historical track features
      if (this.renderOptions.showHistorical &&
          track.historicalPoints &&
          track.historicalPoints.length >= 2) {
        const coordinates = track.historicalPoints.map(p => [p.lng, p.lat]);
        historicalFeatures.push({
          type: 'Feature',
          properties: {
            pilotId: track.pilotId,
            pilotName: track.pilotName,
            color: track.color,
            trackType: 'historical'
          },
          geometry: {
            type: 'LineString',
            coordinates
          }
        });
      }
    });

    // Update live tracks source
    const liveSource = this.map.getSource('pilot-tracks-live') as GeoJSONSource;
    if (liveSource) {
      // Updating live tracks
      liveSource.setData({
        type: 'FeatureCollection',
        features: liveFeatures
      });
    }

    // Update historical tracks source
    const historicalSource = this.map.getSource('pilot-tracks-historical') as GeoJSONSource;
    if (historicalSource) {
      historicalSource.setData({
        type: 'FeatureCollection',
        features: historicalFeatures
      });
    }
  }

  setRenderOptions(options: Partial<TrackRenderOptions>) {
    this.renderOptions = { ...this.renderOptions, ...options };
    this.updateTrackRendering();
  }

  clearTrack(pilotId: string) {
    this.tracks.delete(pilotId);
    this.updateTrackRendering();
  }

  clearAllTracks() {
    this.tracks.clear();
    this.historicalDataCache.clear();
    this.updateTrackRendering();
  }

  removePilot(pilotId: string) {
    this.tracks.delete(pilotId);
    this.updateTrackRendering();
  }

  getTrack(pilotId: string): PilotTrack | undefined {
    return this.tracks.get(pilotId);
  }

  getAllTracks(): Map<string, PilotTrack> {
    return this.tracks;
  }

  // Handle style changes
  onStyleChange() {
    if (!this.map) {
      // No map available
      return;
    }

    // Clear any existing timeout to debounce multiple rapid style changes
    if (this.styleChangeTimeout) {
      clearTimeout(this.styleChangeTimeout);
      this.styleChangeTimeout = null;
    }

    // Sources are reinitialized below regardless of existing state

    // Processing style change

    // List current tracks for debugging
    this.tracks.forEach((track, pilotId) => {
      // Track data preserved
    });

    // Debounce and wait for the style to be fully loaded before re-initializing
    this.styleChangeTimeout = setTimeout(() => {
      const checkStyleLoaded = () => {
        if (this.map && this.map.isStyleLoaded()) {
          // Style loaded, checking layers

          // Only reinitialize if sources don't exist
          if (!this.map.getSource || !this.map.getSource('pilot-tracks-live')) {
            // Reinitializing sources
            this.initializeLayers();
          } else {
            // Updating rendering
            this.updateTrackRendering();
          }

          this.styleChangeTimeout = null;
        } else {
          // Style not ready, retrying
          // Retry after a short delay
          this.styleChangeTimeout = setTimeout(checkStyleLoaded, 100);
        }
      };

      checkStyleLoaded();
    }, 500); // Wait 500ms to ensure style is settled
  }
}